import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';
import AbbrTip from '@/components/AbbrTip';

const FAQS = [
  {
    q: 'What is the difference between qualified and ordinary dividends?',
    a: 'Qualified dividends are taxed at the preferential long-term capital gains rate (0%, 15%, or 20% depending on income). Ordinary dividends are taxed at your regular income tax rate (10-37%). For most middle-income investors, qualified dividends are taxed at 15% while ordinary dividends could be taxed at 22-32%. The difference compounds significantly over decades.',
  },
  {
    q: 'What makes a dividend "qualified"?',
    a: 'Two requirements. First, the payer must be a US corporation or a qualified foreign corporation. Second, you must hold the stock for more than 60 days during the 121-day window centered on the ex-dividend date. Day-traders and short holders default to ordinary tax treatment because they never hit the holding-period test.',
  },
  {
    q: 'Are REIT and BDC dividends qualified?',
    a: 'Mostly no. REIT dividends are largely ordinary (with a small Section 199A deduction available). BDC dividends are entirely ordinary. Bond fund distributions are interest income, also ordinary. Covered-call ETF distributions (JEPI, QYLD, etc.) are a mix and often largely ordinary. This is why the same headline yield can leave you with very different after-tax dollars depending on what produced it.',
  },
  {
    q: 'How do I know what was qualified on my 1099-DIV?',
    a: 'Box 1a on Form 1099-DIV is "Total Ordinary Dividends" (everything). Box 1b is "Qualified Dividends" — the subset that gets the preferential rate. Your tax software automatically applies the lower rate to Box 1b. If you want a per-position breakdown, your broker\'s annual tax statement usually itemises it.',
  },
  {
    q: 'Does it matter for IRAs and 401(k)s?',
    a: 'No — and that is exactly why high-yielders like REITs and BDCs are best held in tax-advantaged accounts. Inside an IRA, every dividend is tax-deferred (traditional IRA) or tax-free at withdrawal (Roth IRA). The qualified/ordinary distinction only matters in regular taxable brokerage accounts.',
  },
];

export default function LearnQualifiedDividends() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="Qualified vs Ordinary Dividends — 2026 Tax Guide for Investors | DiviDrip"
      description="Guide to qualified dividend tax rules — holding period, eligible payers, REIT/BDC quirks, and how to structure accounts for the best after-tax yield."
      canonical="https://twylightcrow.com/learn/qualified-vs-ordinary-dividends"
      guidePath="/learn/qualified-vs-ordinary-dividends"
      sectionLabel="Tax Strategy"
      sectionAnchor="tax"
      kicker="Learn · Dividend taxes"
      h1="Qualified vs Ordinary Dividends — The 2026 Tax Guide"
      datePublished="2026-05-23"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/international-dividends', label: 'International dividends — ADRs & withholding tax' },
        { to: '/learn/mlps-explained', label: 'MLPs explained — K-1 forms and the IRA trap' },
        { to: '/learn/reits-vs-bdcs-vs-cefs', label: 'REITs vs BDCs vs CEFs — high-yield vehicles compared' },
        { to: '/learn/drip-vs-cash-dividends', label: 'DRIP vs cash dividends — which to choose?' },
        { to: '/learn/yield-on-cost', label: 'Yield on cost explained' },
        { to: '/learn/what-is-drip', label: 'What is DRIP?' },
        { to: '/glossary', label: 'Glossary — every dividend term defined' },
      ]}
    >
      <p>
        Two investors can earn the exact same $10,000 in dividends and end up with very
        different after-tax dollars. The difference is whether those dividends are
        <strong> qualified</strong> or <strong> ordinary</strong> — a single checkbox
        on the IRS rulebook that quietly governs maybe a third of your real return.
      </p>

      <h2>The headline tax rates (2026)</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Single filer income</th>
            <th className="text-left font-semibold">Qualified rate</th>
            <th className="text-left font-semibold">Ordinary rate</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Under $49,450</td>
            <td className="text-emerald-600 dark:text-emerald-400">0%</td>
            <td>10-12%</td>
          </tr>
          <tr>
            <td>$49,450 — $545,500</td>
            <td className="text-emerald-600 dark:text-emerald-400">15%</td>
            <td>22-35%</td>
          </tr>
          <tr>
            <td>Over $545,500</td>
            <td className="text-emerald-600 dark:text-emerald-400">20%</td>
            <td>37%</td>
          </tr>
        </tbody>
      </table>
      <p className="text-xs text-muted-foreground italic">
        Thresholds shown are the 2026 single-filer brackets for long-term capital gains /
        qualified dividends, per the IRS COLA release. Married-filing-jointly thresholds
        are roughly 2× the single brackets. Excludes the 3.8% <AbbrTip term="NIIT">NIIT</AbbrTip> on high earners.
        Check the official IRS tables for your exact filing situation.
      </p>

      <h2>The two qualification rules</h2>
      <ol>
        <li>
          <strong>Eligible payer:</strong> a US corporation OR a qualified foreign
          corporation (one whose country has a tax treaty with the US, or whose ADR trades
          on a US exchange). REITs, BDCs, and most foreign-domiciled funds fail this
          test by default.
        </li>
        <li>
          <strong>Holding period:</strong> you must hold the stock for more than 60 days
          during the 121-day window starting 60 days BEFORE the ex-dividend date. In
          practice: buy a week before ex-div, sell two weeks after, and you almost
          certainly miss the qualification window. Long-term holders are automatically
          qualified.
        </li>
      </ol>

      <h2>Where the surprises hide</h2>
      <ul>
        <li><strong>REITs (O, STAG, PLD)</strong> — ordinary, with a small 199A pass-through deduction.</li>
        <li><strong>BDCs (MAIN, ARCC, OBDC)</strong> — ordinary, no deduction. Worst tax treatment.</li>
        <li><strong>Bond ETFs (AGG, BND)</strong> — distributions are interest, taxed as ordinary income.</li>
        <li><strong>Covered-call ETFs (JEPI, JEPQ, QYLD)</strong> — most of the distribution is from options premiums = ordinary income. A common shock for new buyers expecting "dividend ETF" tax treatment.</li>
        <li><strong>Standard dividend stocks (JNJ, KO, PG, MSFT)</strong> — qualified if you hold 60+ days. This is the sweet spot.</li>
        <li><strong>ETFs holding qualified stocks (SCHD, VYM, VOO)</strong> — qualified, passed through.</li>
      </ul>

      <h2>The account-placement playbook</h2>
      <p>
        Once you understand qualified vs ordinary, the right account placement becomes
        obvious:
      </p>
      <ul>
        <li><strong>Roth IRA</strong> — best for ordinary-taxed high-yielders (BDCs, REITs, CEFs, covered-call ETFs). Tax-free growth + tax-free withdrawals at retirement.</li>
        <li><strong>Traditional IRA / 401(k)</strong> — also fine for ordinary-taxed assets. Tax-deferred, but you pay ordinary rate on withdrawal anyway, so the deferral is the only win.</li>
        <li><strong>Taxable brokerage</strong> — best for qualified-eligible holdings (SCHD, VOO, VYM, individual dividend kings). The 15% qualified rate is hard to beat.</li>
        <li><strong>HSA</strong> — if you have access, even better than Roth for any asset class.</li>
      </ul>

      <h2>The simplest after-tax math</h2>
      <p>
        Two investors each earning a 4% yield on $100,000:
      </p>
      <ul>
        <li>
          <strong>Investor A</strong> — SCHD in taxable. $4,000 dividend, qualified, taxed
          at 15% = <strong>$3,400 after tax</strong>.
        </li>
        <li>
          <strong>Investor B</strong> — BDC in taxable, 22% bracket. $4,000 dividend,
          ordinary, taxed at 22% = <strong>$3,120 after tax</strong>.
        </li>
      </ul>
      <p>
        Same headline yield, $280/year difference. Over 30 years and reinvested, the gap
        compounds into tens of thousands. This is the entire reason &ldquo;tax location&rdquo;
        matters as much as &ldquo;asset allocation&rdquo;.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Track it in DiviDrip</h2>
      <p>
        DiviDrip&apos;s Year-End Report (under <Link to="/?view=portfolio">My Portfolio</Link>{' '}
        → Tool Box) totals your annual dividends by ticker so you can match them against
        your 1099-DIV at tax time. Combine that with the per-ticker qualified status
        from your broker&apos;s statement and you have a clean view of your true
        after-tax yield by position.
      </p>
      <p className="text-xs text-muted-foreground italic mt-6">
        Not tax advice. Tax laws change; speak to a tax professional for your specific
        situation.
      </p>
    </LearnPage>
  );
}
